import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdminReservationCardComponent } from '../admin-reservation-card/admin-reservation-card.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiResponse } from '../../../_models/api-response.model';
import { ReservationAdminService } from '../../../_services/api/reservation-admin.service';
import { ReservationList, ReservationSearchCriteria } from '../../../_models/reservation.model';
import { PaginationComponent } from '../../../_shared_components/pagination/pagination.component';

@Component({
  selector: 'app-admin-reservations-list',
  imports: [CommonModule, FormsModule, AdminReservationCardComponent, PaginationComponent],
  templateUrl: './admin-reservations-list.component.html',
  styleUrl: './admin-reservations-list.component.scss',
})
export class AdminReservationsListComponent implements OnInit, OnDestroy {
  showFilterPopup: boolean = false;
  reservations: ReservationList[] = [];
  isLoading = false;
  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  
  searchTerm = '';
  private searchSubject = new Subject<string>();
  selectedStatus: string = 'all';
  selectedSort: string = 'createdAt,desc';
  private destroy$ = new Subject<void>();

  constructor(
    private reservationService: ReservationAdminService,    
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadReservations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadReservations();
    });
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadReservations();
  }

  onSortChange(): void {
    this.currentPage = 0;
    this.loadReservations();
  }

  loadReservations(): void {
    this.isLoading = true;
    
    const criteria: ReservationSearchCriteria = {};
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    const [sortField, sortDirection] = this.selectedSort.split(',');
    this.reservationService.getAllReservations(criteria, this.currentPage, this.pageSize, sortField, sortDirection)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          this.reservations = response.data?.content || [];
          this.totalCount = response.data?.totalElements || 0;
          this.totalPages = response.data?.totalPages || 0;
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        },
        error: (error) => {
          console.error('Error loading blogs:', error);
          this.isLoading = false;
        }
      });
  }

  addNewArticle(): void {
    this.router.navigate(['/admin/articles/create']);
  }

  onReservationUpdated(): void {
    this.loadReservations();
  }

  onReservationDeleted(): void {
    this.loadReservations();
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.currentPage = page;
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setTimeout(() => {
      this.loadReservations();
    }, 1000);
  }
  
  toggleFilterPopup() {
    this.showFilterPopup = !this.showFilterPopup;
  }

  closeFilterPopup(event: Event) {
    if ((event.target as HTMLElement).classList.contains('filter-overlay')) {
      this.showFilterPopup = false;
    }
  }

  private updateQueryParams(params: any) {
    const queryParams = { ...this.route.snapshot.queryParams };
    
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === '') {
        delete queryParams[key];
      } else {
        queryParams[key] = params[key];
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
